// Modules
import {
  ApolloClient as BaseApolloClient,
  ApolloLink,
  createHttpLink,
  DefaultContext,
  DocumentNode,
  from,
  InMemoryCache,
  LazyQueryHookOptions,
  LazyQueryResultTuple,
  MutationHookOptions,
  MutationTuple,
  NormalizedCacheObject,
  Observable,
  Operation,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";

import { errorLink } from "/util/error";

import { auth } from "../../apollo/client/local";

/**
 * Operations that are allowed without authentication.
 * These are typically signup-related operations that must work before a user has a token.
 */
const ALLOWED_UNAUTHENTICATED_OPERATIONS = ["CreatePatient"];

/**
 * Checks if an operation is allowed without authentication.
 */
const isAllowedUnauthenticatedOperation = (operationName: string | undefined): boolean => {
  return !!operationName && ALLOWED_UNAUTHENTICATED_OPERATIONS.includes(operationName);
};

const uriWithQueryParams = (operation: Operation): string => {
  const baseUri = `${process.env.SERVICE_LAYER}/graphql`;
  const queryName = operation.operationName || "";
  const clientName = "scmd-web";
  return `${baseUri}?query_name=${queryName}&client=${clientName}`;
};

const httpLink = createHttpLink({
  uri: uriWithQueryParams,
});

/**
 * Auth guard link that blocks unauthenticated requests to b2c-graphql,
 * except for whitelisted operations (e.g., signup).
 *
 * This prevents sending requests with "Bearer null" tokens which cause
 * "Invalid" authentication errors in DataDog logs.
 */
const authGuardLink = new ApolloLink((operation, forward) => {
  const { token } = auth.read();
  const operationName = operation.operationName;

  // Allow whitelisted operations without authentication
  if (isAllowedUnauthenticatedOperation(operationName)) {
    return forward(operation);
  }

  // Block requests when no valid token exists
  if (!token) {
    return new Observable((observer) => {
      observer.error(
        new Error("Not authenticated: No valid token available for b2c-graphql request"),
      );
      observer.complete();
    });
  }

  return forward(operation);
});

const authLink = setContext((operation, { headers }) => {
  const token = auth.read().token;

  return {
    headers: {
      ...headers,
      // Only add authorization header if token exists
      ...(token && { authorization: `Bearer ${token}` }),
      "x-app-key": process.env.SERVICE_LAYER_APP_KEY,
    },
  };
});

export const createB2CClient = (): BaseApolloClient<NormalizedCacheObject> => {
  const client = new BaseApolloClient({
    cache: new InMemoryCache(),
    link: from([errorLink, authGuardLink, authLink, httpLink]),
    connectToDevTools: process.env.NODE_ENV === "development",
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
    },
  });

  return client;
};

const client = createB2CClient();

export default client;
export type ApolloClient = typeof client;

// Modify the query to automatically use the new client as the parameter
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useB2CQuery: <TData, TVariables extends OperationVariables = any>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: Omit<QueryHookOptions<TData, TVariables>, "client">,
) => QueryResult<TData, TVariables> = (query, options) =>
  useQuery(query, {
    client,
    ...options,
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useB2CLazyQuery: <TData, TVariables extends OperationVariables = any>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: Omit<LazyQueryHookOptions<TData, TVariables>, "client">,
) => LazyQueryResultTuple<TData, TVariables> = (query, options) =>
  useLazyQuery(query, {
    client,
    ...options,
  });

//Modify the mutation to automatically use the new client as the parameter
export const useB2CMutation: <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TContext = DefaultContext,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: Omit<MutationHookOptions<TData, TVariables, TContext>, "client">,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => MutationTuple<TData, TVariables, TContext, any> = (mutation, options) =>
  useMutation(mutation, {
    client,
    ...options,
  });

export interface B2CError {
  extensions?: {
    status_code?: number;
  };
  path?: (number | string)[];
}

export interface B2CQueryResult<TData, TVariables extends OperationVariables>
  extends QueryResult<TData, TVariables> {
  errors?: B2CError[];
}
